import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { BACKEND_URL } from './constants';

export interface ApiResult<T = any> {
  ok: boolean;
  status: number;
  data: T;
  /** The backend's machine-readable refusal, e.g. "last_admin". */
  code?: string;
  /** The backend's human-readable message, or a generic one. */
  error?: string;
}

/**
 * Calls the /api/org and /api/invitations endpoints (enterprise batches 7-8)
 * and always resolves: a refusal comes back as `{ ok: false, code, error }`
 * so a page can show the server's own wording ("A firm must keep at least
 * one admin") rather than a generic failure.
 */
export function useOrgApi() {
  const { getAccessTokenSilently } = useAuth0();
  return useCallback(
    async <T = any>(method: string, path: string, body?: unknown): Promise<ApiResult<T>> => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
        });
        const res = await fetch(`${BACKEND_URL}/api${path}`, {
          method,
          headers: {
            Authorization: `Bearer ${token}`,
            ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
          },
          credentials: 'include',
          body: body !== undefined ? JSON.stringify(body) : undefined,
        });
        let data: any = null;
        try {
          data = await res.json();
        } catch {
          data = null;
        }
        if (res.ok) return { ok: true, status: res.status, data };
        return {
          ok: false,
          status: res.status,
          data,
          code: data?.code,
          error: data?.error || `Something went wrong (${res.status}). Please try again.`,
        };
      } catch {
        return { ok: false, status: 0, data: null as any, error: 'Could not reach the server. Please try again.' };
      }
    },
    [getAccessTokenSilently]
  );
}

export const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
};

export const ROLE_HELP: Record<string, string> = {
  admin: 'Everything a member can do, plus manage the team and the firm settings.',
  member: 'Create, edit and delete any of the firm\'s deals.',
  viewer: 'See every deal and export it, but not change anything.',
};

/** "Oct 14, 2026", or an em-free fallback for a missing date. */
export function formatDate(iso?: string | null): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'Never';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
